import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo } from 'react';

type Json = unknown;
type Body = Json | undefined;

export class ApiError extends Error {
  constructor(
    public method: string,
    public path: string,
    public status: number,
    public detail?: unknown,
  ) {
    super(`API ${method} ${path} → ${status}`);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  readonly isLoaded: boolean;
  readonly isSignedIn: boolean;
  get<T = Json>(path: string): Promise<T>;
  post<T = Json>(path: string, body?: Body): Promise<T>;
  patch<T = Json>(path: string, body?: Body): Promise<T>;
  del<T = Json>(path: string): Promise<T>;
}

/**
 * Token-attaching fetch wrapper. Awaits `isLoaded` to avoid Clerk's token race
 * (PRD §10). Throws ApiError on non-2xx so callers can handle status codes.
 */
export function useApiClient(): ApiClient {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const request = useCallback(
    async <T,>(method: string, path: string, body?: Body): Promise<T> => {
      if (!isLoaded) {
        throw new ApiError(method, path, 0, 'auth not loaded');
      }
      const token = await getToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        let detail: unknown;
        try {
          detail = await res.json();
        } catch {
          /* body not JSON — fine */
        }
        throw new ApiError(method, path, res.status, detail);
      }

      // 204 No Content
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },
    [getToken, isLoaded],
  );

  return useMemo(
    () => ({
      isLoaded,
      isSignedIn: !!isSignedIn,
      get: (path) => request('GET', path),
      post: (path, body) => request('POST', path, body),
      patch: (path, body) => request('PATCH', path, body),
      del: (path) => request('DELETE', path),
    }),
    [isLoaded, isSignedIn, request],
  );
}
